import type { ApplePayConfig, ApplePayInstance, RecurlyError } from '@recurly/recurly-js';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import type ProductType from '@alltrails/amplitude/enums/ProductType';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { ApiError } from '@alltrails/core/utils/api';
import logError from '@alltrails/core/utils/logError';
import { executeRecaptcha } from '@alltrails/core/utils/recaptcha-helpers';
import { useExperiments } from '@alltrails/experiments';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import useLoadApplePaySDK from '../../hooks/useLoadApplePaySDK';
import type ProResponse from '../../types/ProResponse';
import styles from './ApplePayButtonWithSDK.module.scss';

type Props = {
  applePayConfig: ApplePayConfig;
  currencyCode: CurrencyCode;
  onApplePayToken: ({ apple_pay_token, recaptcha }: { apple_pay_token: string; recaptcha: string }) => void;
  roundedCorners?: boolean;
  setErrors: (errors: ProResponse['errors']) => void;
  setSubmitting: (submitting: boolean) => void;
  showContinueWith?: boolean;
  productType: ProductType;
  webProPurchaseLocation: WebProPurchaseLocation;
  validateCompliance: (paymentMethod: PaymentMethod) => boolean;
};

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1
export default function ApplePayButtonWithSDK({
  applePayConfig,
  currencyCode,
  onApplePayToken,
  productType,
  roundedCorners = false,
  setErrors,
  setSubmitting,
  showContinueWith = false,
  webProPurchaseLocation,
  validateCompliance
}: Props) {
  const [canDisplayApplePayButton, setCanDisplayApplePayButton] = useState<boolean | null>(null);
  const [applePayInstance, setApplePayInstance] = useState<ApplePayInstance>();
  const [applePayReady, setApplePayReady] = useState(false);
  // Tracks whether the Apple Pay flow has been initiated (which would trigger the native Apple Pay modal),
  // so that we can prevent the user from tapping the purchase button multiple times
  const [isInitiating, setIsInitiating] = useState(false);
  const isInitiatingRef = useRef(false);

  const experiments = useExperiments();
  const isDoubleSubmitGuardEnabled = experiments['web-growth-rm-apple-pay-disable-on-click']?.value === 'on';

  const resetInitiating = () => {
    isInitiatingRef.current = false;
    setIsInitiating(false);
  };

  const languageRegionCode = useLanguageRegionCode();

  const isApplePaySDKLoaded = useLoadApplePaySDK();

  useEffect(() => {
    if (isApplePaySDKLoaded) {
      if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
        setCanDisplayApplePayButton(true);
      } else {
        setCanDisplayApplePayButton(false);
      }
    }
  }, [isApplePaySDKLoaded]);

  useEffect(() => {
    // https://github.com/recurly/recurly-js/blob/4e2789fd7e5a8e07e8dcd5a204a25ca92894ba68/lib/recurly/apple-pay/apple-pay.js#L57
    if (isApplePaySDKLoaded && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);

      const applePay = recurly.ApplePay({
        country: 'US', // this is the merchant's country code, so will remain US
        currency: currencyCode,
        label: 'AllTrails',
        ...applePayConfig
      });

      applePay.on('error', (error: RecurlyError) => {
        resetInitiating();
        logError(error);
        logPurchaseFailed({ error_code: error.message, payment_method: 'apple_pay' });
      });

      applePay.on('cancel', () => {
        resetInitiating();
      });

      applePay.on('token', async token => {
        try {
          setSubmitting(true);

          const recaptcha = await executeRecaptcha('userPaymentAction');

          await onApplePayToken({ apple_pay_token: token.id, recaptcha });
        } catch (e) {
          logPurchaseFailed({
            ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
            payment_method: 'apple_pay'
          });

          if (e instanceof ApiError) {
            setErrors(e.data);
          }

          setSubmitting(false);
          resetInitiating();
        }
      });

      applePay.ready(() => {
        setApplePayReady(true);
      });

      setApplePayInstance(applePay);
    }
  }, [currencyCode, onApplePayToken, setErrors, setSubmitting, applePayConfig, isApplePaySDKLoaded]);

  const applePayButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const applePayButton = applePayButtonRef.current;

    if (applePayButton) {
      const handleClick = () => {
        // Prevent double submission if the user taps the button again while the Apple Pay modal is loading
        if (isDoubleSubmitGuardEnabled && isInitiatingRef.current) {
          return;
        }

        logPurchaseButtonTapped({ payment_method: 'apple_pay', product_type: productType, web_purchase_location: webProPurchaseLocation });

        // Validate compliance checkbox before proceeding
        if (!validateCompliance('apple_pay')) {
          return; // Block payment if validation fails
        }

        if (applePayReady && applePayInstance) {
          if (isDoubleSubmitGuardEnabled) {
            isInitiatingRef.current = true;
            setIsInitiating(true);
          }
          applePayInstance.begin();
        }
      };

      applePayButton.addEventListener('click', handleClick);

      return () => {
        if (applePayButton) {
          applePayButton.removeEventListener('click', handleClick);
        }
      };
    }
    return undefined;
  }, [applePayReady, applePayInstance, productType, webProPurchaseLocation, isApplePaySDKLoaded, validateCompliance, isDoubleSubmitGuardEnabled]);

  // Show placeholder only while SDK is loading
  if (canDisplayApplePayButton === null) {
    return <div className={styles.placeholder} />;
  }

  if (canDisplayApplePayButton) {
    const showDisabled = isDoubleSubmitGuardEnabled && isInitiating;

    return (
      <div
        className={classNames(styles.applePayButtonContainer, {
          [styles.roundedCorners]: roundedCorners,
          [styles.disabled]: showDisabled
        })}
        aria-disabled={showDisabled || undefined}
      >
        <apple-pay-button
          buttonstyle="black"
          type={showContinueWith ? 'continue' : 'plain'}
          // Full locale support https://developer.apple.com/documentation/applepayontheweb/applepaybuttonlocale
          locale={languageRegionCode}
          ref={applePayButtonRef}
        />
      </div>
    );
  }

  return null;
}
